import React, { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import ChartCard from '../../components/charts/ChartCard';
import WindRose from '../../components/charts/WindRose';
import Banner from '../../components/general/Banner';
import DownloadButton from '../../components/general/DownloadButton';
import EmptyPeriod from '../../components/general/EmptyPeriod';
import RangeToolbar from '../../components/general/RangeToolbar';
import Spinner from '../../components/general/Spinner';
import StatusPill, { useAgo } from '../../components/general/StatusPill';
import Page from '../../components/layout/Page';
import PageHeader from '../../components/layout/PageHeader';
import KpiCard, { Kpi } from '../../components/weather/KpiCard';
import { axisLabels, bucketLabel, pointLabels } from '../../components/weather/weather-view';
import { useCsvDownload } from '../../hooks/useCsvDownload';
import { useDeviceDetail } from '../../hooks/useDeviceDetail';
import { useTranslation } from '../../i18n/i18n';
import { DeviceType } from '../../models/device';
import { Period, presetPeriod } from '../../models/time-range';
import { geoDetailUrl, weatherListUrl } from '../../routes.constants';
import { formatDelta, formatFixed } from '../../utils/format';
import { latestOf, readString, toWeatherSample, WeatherSample } from '../../utils/payload';
import { bucketize, bucketMsFor, finite, rainIncrements } from '../../utils/series';
import { compassName, MS_TO_KNOTS, windRose } from '../../utils/wind';
import './WeatherDetailPage.scss';

const WeatherDetailPage = () => {
  const { deviceUuid = '' } = useParams();
  const { t } = useTranslation('weather');
  const { t: tc } = useTranslation('common');
  const ago = useAgo();
  const [period, setPeriod] = useState<Period>(() => presetPeriod('24h'));
  const [hover, setHover] = useState<number | null>(null);
  const { device, latest, window, loading, error, reload } = useDeviceDetail(deviceUuid, period.from, period.to);
  const spanMs = window ? window.to.getTime() - window.from.getTime() : 0;
  const csv = useCsvDownload(deviceUuid, device?.name ?? '');

  const samples = useMemo<WeatherSample[]>(() => (window?.readings ?? []).map(toWeatherSample), [window]);

  const buckets = useMemo(
    () => (window ? bucketize(samples, window.from, window.to, bucketMsFor(spanMs)) : undefined),
    [samples, window, spanMs],
  );

  const kpis = useMemo<Kpi[]>(() => {
    const empty = tc('empty');
    // Latest value against the first one of the period.
    const vs = (key: 'temperature' | 'humidity' | 'pressure') => {
      const now = latestOf(samples, key);
      const start = samples.find((s) => s[key] !== undefined)?.[key];
      return now && start !== undefined && now.index > 0
        ? t('kpi.vsStart', { delta: formatDelta(now.value - start, 1) })
        : empty;
    };
    const temperature = latestOf(samples, 'temperature');
    const humidity = latestOf(samples, 'humidity');
    const pressure = latestOf(samples, 'pressure');
    const wind = latestOf(samples, 'windSpeed');
    const direction = latestOf(samples, 'windDirection');
    const counter = latestOf(samples, 'rainfallCounter');
    const rainTotal = latestOf(samples, 'rainfallTotal');
    const rain = finite(rainIncrements(samples)).reduce((a, b) => a + b, 0);
    const battery = latestOf(samples, 'battery');
    let batteryChange: Date | undefined;
    if (battery) {
      for (let i = battery.index - 1; i >= 0; i--) {
        const b = samples[i].battery;
        if (b !== undefined && b !== battery.value) {
          batteryChange = samples[i + 1].time;
          break;
        }
      }
    }
    return [
      { label: t('kpi.temperature'), value: formatFixed(temperature?.value, 1), unit: '°C', sub: vs('temperature') },
      { label: t('kpi.humidity'), value: formatFixed(humidity?.value, 1), unit: '%', sub: vs('humidity') },
      { label: t('kpi.pressure'), value: formatFixed(pressure?.value, 1), unit: 'hPa', sub: vs('pressure') },
      {
        label: t('kpi.wind'),
        value: formatFixed(wind?.value, 1),
        unit: 'm/s',
        sub:
          wind && direction
            ? t('kpi.windSub', {
                knots: (wind.value * MS_TO_KNOTS).toFixed(1),
                deg: direction.value,
                dir: compassName(direction.value),
              })
            : empty,
      },
      {
        label: t('kpi.rain'),
        value: rainTotal ? rain.toFixed(2) : empty,
        unit: 'mm',
        sub:
          counter || rainTotal
            ? t('kpi.rainSub', { counter: counter?.value ?? empty, total: formatFixed(rainTotal?.value, 2) })
            : empty,
      },
      {
        label: t('kpi.battery'),
        value: formatFixed(battery?.value, 0),
        unit: '%',
        sub: battery
          ? batteryChange
            ? t('kpi.batterySub', { ago: ago(batteryChange) })
            : t('kpi.batteryStable')
          : empty,
      },
    ];
  }, [samples, t, tc, ago]);

  const rose = useMemo(
    () => windRose(samples.map((s) => ({ speed: s.windSpeed, direction: s.windDirection }))),
    [samples],
  );
  const speeds = finite(samples.map((s) => s.windSpeed));
  const meanSpeed = speeds.length ? speeds.reduce((a, b) => a + b, 0) / speeds.length : undefined;

  const changePeriod = (p: Period) => {
    setHover(null);
    setPeriod(p);
  };

  if (device && device.type === DeviceType.GEO) return <Navigate to={geoDetailUrl(device.uuid)} replace />;

  const name = device?.name ?? '';
  const devEui = latest ? readString(latest.payload, 'devEUI') : undefined;
  const meta = [
    { label: tc('uuid'), value: deviceUuid },
    ...(devEui ? [{ label: t('devEui'), value: devEui }] : []),
    ...(latest?.topic ? [{ label: tc('topic'), value: latest.topic }] : []),
  ];

  const charts = buckets && window && (
    <>
      {(
        [
          { key: 'temperature', title: t('charts.temperature'), unit: '°C', decimals: 1 },
          { key: 'humidity', title: t('charts.humidity'), unit: '%', decimals: 1 },
          { key: 'pressure', title: t('charts.pressure'), unit: 'hPa', decimals: 1 },
          { key: 'windSpeed', title: t('charts.windSpeed'), unit: 'm/s', decimals: 1 },
        ] as const
      ).map((c) => (
        <ChartCard
          key={c.key}
          kind="line"
          title={c.title}
          unit={c.unit}
          decimals={c.decimals}
          values={buckets[c.key]}
          pointLabels={pointLabels(buckets.starts, spanMs)}
          axisLabels={axisLabels(buckets.starts, window.to, spanMs)}
          hover={hover}
          onHover={setHover}
        />
      ))}
      <ChartCard
        kind="bar"
        title={t('charts.rainfall')}
        unit={t('charts.rainUnit', { bucket: bucketLabel(buckets.bucketMs) })}
        decimals={2}
        values={buckets.rain}
        pointLabels={pointLabels(buckets.starts, spanMs)}
        axisLabels={axisLabels(buckets.starts, window.to, spanMs)}
        hover={hover}
        onHover={setHover}
      />
      <ChartCard
        kind="line"
        title={t('charts.battery')}
        unit="%"
        decimals={0}
        domain={[0, 100]}
        values={buckets.battery}
        pointLabels={pointLabels(buckets.starts, spanMs)}
        axisLabels={axisLabels(buckets.starts, window.to, spanMs)}
        hover={hover}
        onHover={setHover}
      />
      <WindRose rose={rose} latestDirection={latestOf(samples, 'windDirection')?.value} meanSpeed={meanSpeed} />
    </>
  );

  return (
    <Page variant="detail">
      <PageHeader
        crumbs={[{ label: tc('nav.weather'), to: weatherListUrl }, { label: name || deviceUuid }]}
        title={name || ' '}
        titleAside={<StatusPill latest={latest} />}
        meta={meta}
        actions={
          <DownloadButton onClick={csv.download} loading={csv.downloading} progress={csv.progress} disabled={!latest} />
        }
      />

      <RangeToolbar
        value={period}
        onChange={changePeriod}
        first={samples[0]?.time}
        last={samples[samples.length - 1]?.time}
        count={window ? samples.length : undefined}
      />

      {error !== undefined && !loading && (
        <Banner
          kind="error"
          action={
            <button type="button" onClick={reload}>
              {tc('retry')}
            </button>
          }
        >
          {tc('errors.generic')}
        </Banner>
      )}
      {csv.error !== undefined && <Banner kind="error">{tc('errors.csv')}</Banner>}
      {window?.truncated && <Banner kind="info">{tc('range.truncated', { count: samples.length })}</Banner>}

      {loading && !window ? (
        <div className="WeatherDetailPage__loading">
          <Spinner />
        </div>
      ) : (
        window &&
        (samples.length === 0 ? (
          <EmptyPeriod />
        ) : (
          <>
            <section aria-label={t('latest')} className="WeatherDetailPage__kpis">
              {kpis.map((k) => (
                <KpiCard key={k.label} {...k} />
              ))}
            </section>
            <section aria-label={t('charts.label')} className="WeatherDetailPage__charts">
              {charts}
            </section>
          </>
        ))
      )}
    </Page>
  );
};

export default WeatherDetailPage;
